import { useState, useEffect, useRef } from 'react';
import apiService from '../services/api';

// Shared state to prevent multiple simultaneous API calls across all hook instances
let fetchInProgress = false;
let fetchPromise = null;

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonatedBy, setImpersonatedBy] = useState(null);
  const [originalAdminToken, setOriginalAdminToken] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations using ref (more reliable than state)
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    const token = localStorage.getItem('token');
    if (token) {
      fetchCurrentUser(token);
    } else {
      setLoading(false);
    }
    setIsInitialized(true);
  }, []); // Empty dependency array - only run once

  const fetchCurrentUser = async (token) => {
    // If a fetch is already in progress, wait for it instead of making a new request
    if (fetchInProgress && fetchPromise) {
      try {
        const userData = await fetchPromise;
        setUser(userData);
        setLoading(false);
        return;
      } catch (error) {
        // If the shared request failed, continue with our own request
        console.error('Shared fetch failed, making new request:', error);
      }
    }
    
    // Mark that we're starting a fetch
    fetchInProgress = true;
    
    try {
      // Check if we already have user data and it's recent (within 5 minutes)
      const cachedUser = localStorage.getItem('cachedUser');
      const cacheTime = localStorage.getItem('userCacheTime');
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      
      if (cachedUser && cacheTime && (now - parseInt(cacheTime)) < fiveMinutes) {
        const userData = JSON.parse(cachedUser);
        setUser(userData);
        
        // Check for impersonation data in localStorage even with cached user
        const impersonationData = localStorage.getItem('impersonatedBy');
        if (impersonationData) {
          setImpersonatedBy(JSON.parse(impersonationData));
        }
        
        // Check for original admin token even with cached user
        const adminToken = localStorage.getItem('originalAdminToken');
        if (adminToken) {
          setOriginalAdminToken(adminToken);
        }
        
        setLoading(false);
        fetchInProgress = false;
        fetchPromise = null;
        return;
      }
      
      // Create the promise and store it so other instances can wait for it
      fetchPromise = apiService.getCurrentUser();
      const userData = await fetchPromise;
      
      // Cache the user data
      localStorage.setItem('cachedUser', JSON.stringify(userData));
      localStorage.setItem('userCacheTime', now.toString());
      
      // Check if user is active
      if (!userData.is_active) {
        localStorage.removeItem('token');
        localStorage.removeItem('cachedUser');
        localStorage.removeItem('userCacheTime');
        setUser(null);
        setImpersonatedBy(null);
        setLoading(false);
        fetchInProgress = false;
        fetchPromise = null;
        return;
      }
      
      setUser(userData);
      
      // Check for impersonation data in localStorage
      const impersonationData = localStorage.getItem('impersonatedBy');
      if (impersonationData) {
        setImpersonatedBy(JSON.parse(impersonationData));
      }
      
      // Check for original admin token
      const adminToken = localStorage.getItem('originalAdminToken');
      if (adminToken) {
        setOriginalAdminToken(adminToken);
      }
      
      // Clear the shared state after successful fetch
      fetchInProgress = false;
      fetchPromise = null;
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('cachedUser');
      localStorage.removeItem('userCacheTime');
      localStorage.removeItem('impersonatedBy');
      
      // Clear the shared state on error
      fetchInProgress = false;
      fetchPromise = null;
    }
    setLoading(false);
  };

  const login = async (loginData) => {
    try {
      const data = await apiService.login(loginData);
      localStorage.setItem('token', data.access_token);
      setUser(data.user);
      
      // Clear any cached user data and impersonation data on regular login
      localStorage.removeItem('cachedUser');
      localStorage.removeItem('userCacheTime');
      localStorage.removeItem('impersonatedBy');
      localStorage.removeItem('originalAdminToken');
      setImpersonatedBy(null);
      setOriginalAdminToken(null);
      
      // Reset fetch state to prevent stale shared state
      fetchInProgress = false;
      fetchPromise = null;
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Login failed';
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const loginAsUser = async (userId) => {
    try {
      // Store the current admin token before impersonating
      const currentToken = localStorage.getItem('token');
      if (currentToken && !originalAdminToken) {
        localStorage.setItem('originalAdminToken', currentToken);
        setOriginalAdminToken(currentToken);
      }
      
      const data = await apiService.loginAsUser(userId);
      localStorage.setItem('token', data.access_token);
      setUser(data.user);
      
      // Clear cached user data to ensure fresh impersonation data is loaded
      localStorage.removeItem('cachedUser');
      localStorage.removeItem('userCacheTime');
      
      // Store impersonation data
      if (data.impersonated_by) {
        localStorage.setItem('impersonatedBy', JSON.stringify(data.impersonated_by));
        setImpersonatedBy(data.impersonated_by);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Login as user error:', error);
      
      let errorMessage = 'Failed to login as user';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const stopImpersonation = async () => {
    try {
      if (!originalAdminToken) {
        // If no original admin token, just logout
        logout();
        return { success: true };
      }
      
      // Restore the original admin token
      localStorage.setItem('token', originalAdminToken);
      
      // Clear impersonation data and cached user data
      localStorage.removeItem('impersonatedBy');
      localStorage.removeItem('originalAdminToken');
      localStorage.removeItem('cachedUser');
      localStorage.removeItem('userCacheTime');
      setImpersonatedBy(null);
      setOriginalAdminToken(null);
      
      // Try to fetch the admin user data
      try {
        const userData = await apiService.getCurrentUser();
        setUser(userData);
        return { success: true };
      } catch (fetchError) {
        // If the admin token is expired or invalid, logout
        console.error('Admin token expired or invalid:', fetchError);
        logout();
        return { success: false, error: 'Admin session expired' };
      }
    } catch (error) {
      console.error('Stop impersonation error:', error);
      // If there's an error, just logout
      logout();
      return { success: false, error: 'Failed to restore admin session' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('impersonatedBy');
    localStorage.removeItem('originalAdminToken');
    localStorage.removeItem('cachedUser');
    localStorage.removeItem('userCacheTime');
    setUser(null);
    setImpersonatedBy(null);
    setOriginalAdminToken(null);
  };

  const changePassword = async (passwordData) => {
    try {
      await apiService.changePassword(passwordData);
      return { success: true };
    } catch (error) {
      console.error('Error changing password:', error);
      return { success: false, error: 'Failed to change password' };
    }
  };

  const cancelSubscription = async () => {
    try {
      const subscriptions = await apiService.getUserSubscriptions();
      const activeSubscription = Array.isArray(subscriptions)
        ? subscriptions.find((sub) => sub.status === 'active' && !sub.cancel_at_period_end)
        : null;
      if (!activeSubscription?.id) {
        return { success: false, error: 'No active subscription found' };
      }
      await apiService.cancelSubscription(activeSubscription.id);
      await fetchCurrentUser(localStorage.getItem('token'));
      return { success: true };
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      return { success: false, error: error?.message || 'Failed to cancel subscription' };
    }
  };

  return {
    user,
    loading,
    impersonatedBy,
    login,
    loginAsUser,
    stopImpersonation,
    logout,
    changePassword,
    cancelSubscription
  };
}; 